import cn from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import getMultiplePrintAreasGuidelines from 'editor/src/store/design/selector/getMultiplePrintAreasGuidelines';
import { PrintTechnology } from 'editor/src/store/design/types';
import { useSelector } from 'editor/src/store/hooks';
import getHostSetting from 'editor/src/store/hostSettings/selector/getHostSetting';
import { UNSELECTED_PAGE_COUNT } from 'editor/src/store/variants/reducer/setPageCountReducer';

import { RootState } from 'editor/src/store';

import DrawerHeader from 'editor/src/component/BottomBarMobile/DrawerHeader';
import ContentBlock from 'editor/src/component/DesktopSidebar/TabContents/Elements/ContentBlock';
import DigitizationGuidelines from 'editor/src/component/DesktopSidebar/TabContents/GalleryTabContent/DigitizationGuidelines';
import DesignOptions from 'editor/src/component/DesktopSidebar/TabContents/ProductTabContent/DesignOptions';
import MultiplePrintAreasGuidelines from 'editor/src/component/DesktopSidebar/TabContents/ProductTabContent/MultiplePrintAreasGuidelines';
import ProductInfo from 'editor/src/component/DesktopSidebar/TabContents/ProductTabContent/ProductInfo';
import ProductIntro from 'editor/src/component/DesktopSidebar/TabContents/ProductTabContent/ProductIntro';
import TabContentLoadingBlocks from 'editor/src/component/DesktopSidebar/TabContents/ProductTabContent/TabContentLoadingBlocks';
import UnavailableVariantsInfo from 'editor/src/component/DesktopSidebar/TabContents/ProductTabContent/UnavailableVariantsInfo';
import IconInfo from 'editor/src/component/Icon/IconInfo';
import { useIsMobile } from 'editor/src/component/useDetectDeviceType';

import TabContentHeader from '../../TabContentHeader';

import ECommerceProductContent from './ECommerceProductContent';
import ErrorMessage from './ErrorMessage';
import ManualControls, { hasManualControls } from './ManualControls';
import ProductControlList from './ProductControlList';
import PageCountControl from './ProductControls/PageCountControl';

import styles from './index.module.scss';

interface Props {
  openInfo(): void;
  closeMenuCb?(): void;
}

function selector(state: RootState) {
  const {
    selectedPageCount,
    product,
    isLoaded,
    isVariantFlow,
    configuration,
    variationGroups,
    selectedMultiOptions,
    disableVariationControls,
    disableVariationControlsDescription,
  } = state.variants;

  return {
    selectedPageCount,
    product,
    isLoaded,
    isVariantFlow,
    multiMode: !configuration.singleSelection,
    variationGroups,
    options: selectedMultiOptions,
    hasControls:
      hasManualControls(state) || !!(product.productControls.length + product.externalProductControls.length),
    disableVariationControls,
    disableVariationControlsDescription,
  };
}

function ProductControlContent({ openInfo, closeMenuCb }: Props) {
  const { t } = useTranslation();
  const {
    selectedPageCount,
    product,
    isLoaded,
    isVariantFlow,
    multiMode,
    options,
    hasControls,
    disableVariationControls,
    disableVariationControlsDescription,
  } = useSelector(selector, shallowEqual);
  const isMobile = useIsMobile();
  const { pages, productControls, externalProductControls, confirmOnControlChange } = product;
  const canShowControlList = !pages || selectedPageCount !== UNSELECTED_PAGE_COUNT;
  const showUnavailableVariants = useSelector((state) => state.variants.configuration.showUnavailableVariants);
  const requiresDigization = useSelector(
    (state) =>
      state.design.designData?.spreads[state.editor.currentSpreadIndex]?.technology === PrintTechnology.Embroidery,
  );
  const digitizationRules = useSelector((state) => getHostSetting(state, 'digitizationRules'));
  const showDesignOptions = useSelector((state) => getHostSetting(state, 'designOptions'));
  const multiplePrintAreasGuidelines = useSelector(getMultiplePrintAreasGuidelines);

  const variationGroups = useSelector((state) => state.variants.variationGroups);

  // Optimized variant count calculation with early termination for large datasets
  const selectedVariantsAmount = useMemo(() => {
    if (!variationGroups || variationGroups.length === 0) {
      return 0;
    }

    // For performance, limit counting if we're dealing with large datasets
    const maxCount = 5000; // Stop counting beyond this to prevent UI freezing

    const count = variationGroups.reduce((acc, group) => acc + group.variationsInfo.length, 0);

    if (count > maxCount) {
      // eslint-disable-next-line no-console
      console.warn(`Variant count exceeds ${maxCount}, UI may show approximate count for performance`);
    }

    return count;
  }, [variationGroups]);

  const productVariantsLimit = useSelector((state) => getHostSetting(state, 'variantsLimit'));

  const isLimitOfVariantsExceeded = useMemo(() => {
    if (!productVariantsLimit) {
      return false;
    }
    return selectedVariantsAmount > productVariantsLimit;
  }, [selectedVariantsAmount, productVariantsLimit]);

  const infoButton = useMemo(() => {
    if (hasControls) {
      return (
        <div onClick={openInfo} className={cn(styles.iconInfoContainer, 'cy-product-info-btn')}>
          <IconInfo className={styles.iconInfo} />
        </div>
      );
    }
    return null;
  }, [hasControls, openInfo]);

  return (
    <>
      <ContentBlock noBorder>
        {isMobile ? (
          <DrawerHeader title={t('editor-product')} closeMenu={closeMenuCb}>
            {infoButton}
          </DrawerHeader>
        ) : (
          <TabContentHeader title={t('editor-product')}>{infoButton}</TabContentHeader>
        )}
      </ContentBlock>
      {isLoaded || !isVariantFlow ? (
        <ContentBlock fill scroll className="cy-product-properties">
          {hasControls ? (
            <>
              <ProductIntro title={product.title} categoryTitle={product.categoryTitle} />
              {requiresDigization && digitizationRules?.guidelinesInfo && digitizationRules?.priceInfo && (
                <DigitizationGuidelines
                  guidelinesInfo={digitizationRules.guidelinesInfo}
                  priceInfo={digitizationRules?.priceInfo}
                />
              )}
              {multiplePrintAreasGuidelines && <MultiplePrintAreasGuidelines config={multiplePrintAreasGuidelines} />}
              <ManualControls />
              {pages && <PageCountControl selectedPageCount={selectedPageCount} pages={pages} />}
              {canShowControlList && (
                <ProductControlList
                  multiMode={multiMode}
                  productControls={productControls}
                  externalProductControls={externalProductControls}
                  controlsUIOrder={product.controlsUIOrder}
                  confirmOnControlChange={confirmOnControlChange}
                  disableVariationControls={!!disableVariationControls}
                  disableVariationControlsDescription={disableVariationControlsDescription}
                />
              )}
              {showDesignOptions && multiMode && !disableVariationControls && <DesignOptions />}
              {showUnavailableVariants && <UnavailableVariantsInfo product={product} options={options} />}

              {isLimitOfVariantsExceeded && (
                <ErrorMessage
                  selectedVariantsAmount={selectedVariantsAmount}
                  productVariantsLimit={productVariantsLimit}
                />
              )}
            </>
          ) : (
            <>
              {product.title && (
                <ProductInfo
                  productTitle={product.title}
                  productDescription={product.description}
                  categoryTitle={product.categoryTitle}
                  previewUrls={product.previewUrls}
                />
              )}
              <ManualControls noTopBar />
            </>
          )}
          <ECommerceProductContent />
        </ContentBlock>
      ) : (
        <TabContentLoadingBlocks />
      )}
    </>
  );
}

export default React.memo(ProductControlContent);
